import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  alpha,
  useTheme,
  IconButton,
} from '@mui/material';
import {
  AccountBalance,
  Speed,
  CheckCircle,
  ArrowBack,
  ArrowForward,
  HelpOutline,
  TrendingUp,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import BankStatementUploader from './BankStatementUploader';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const WelcomeFlow = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState('initial'); // 'initial', 'quiz', 'sba-q1', 'sba-q2', 'sba-q3'
  const [hasTaxReturns, setHasTaxReturns] = useState(null);
  const [hasCreditScore, setHasCreditScore] = useState(null);
  const [hasOutstandingDebt, setHasOutstandingDebt] = useState(null);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [redirectMessage, setRedirectMessage] = useState('');

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.goodMorning');
    if (hour < 17) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  // Helper function to build URL with return URL, email, and auth token
  const buildRedirectUrl = async (baseUrl, appType = 'financing') => {
    let targetUrl = baseUrl;
    if (appType === 'pfs') {
      targetUrl = baseUrl.replace(/\/$/, '') + '/es/auth/signin';
    }
    
    const url = new URL(targetUrl);
    const returnUrl = `${import.meta.env.VITE_CORELOANHUB_URL || 'http://localhost:3000'}/dashboard?${appType === 'pfs' ? 'pfs_submitted=true' : 'submitted=true'}`;
    url.searchParams.append('returnUrl', returnUrl);
    
    if (user?.email) {
      url.searchParams.append('email', user.email);
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/auth/generate-single-use-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        url.searchParams.append('authToken', response.data.token);
      }
    } catch (error) {
      console.error('Failed to generate auth token:', error);
    }
    
    return url.toString();
  };

  const handleSBAClick = () => {
    setStep('sba-q1');
  };

  const handleFasterFundingClick = async () => {
    setIsGeneratingToken(true);
    setRedirectMessage(t('welcome.preparingApplication'));
    
    const redirectUrl = await buildRedirectUrl(
      import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com',
      'financing'
    );
    window.location.href = redirectUrl;
  };

  const handleTaxReturnsAnswer = async (answer) => {
    setHasTaxReturns(answer);
    if (answer === 'no') {
      setIsGeneratingToken(true);
      setRedirectMessage(t('welcome.noProblemFasterSolution'));
      setTimeout(async () => {
        const redirectUrl = await buildRedirectUrl(
          import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com',
          'financing'
        );
        window.location.href = redirectUrl;
      }, 1500);
    } else {
      setStep('sba-q2');
    }
  };

  const handleCreditScoreAnswer = async (answer) => {
    setHasCreditScore(answer);
    
    if (answer === 'no') {
      setIsGeneratingToken(true);
      setRedirectMessage(t('welcome.greatOptionsBuildingCredit'));
      setTimeout(async () => {
        const redirectUrl = await buildRedirectUrl(
          import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com',
          'financing'
        );
        window.location.href = redirectUrl;
      }, 1500);
    } else {
      setStep('sba-q3');
    }
  };

  const handleOutstandingDebtAnswer = async (answer) => {
    setHasOutstandingDebt(answer);
    setIsGeneratingToken(true);
    
    if (answer === 'yes') {
      setRedirectMessage(t('welcome.debtConsolidationOptions'));
      setTimeout(async () => {
        const redirectUrl = await buildRedirectUrl(
          import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com',
          'financing'
        );
        window.location.href = redirectUrl;
      }, 1500);
    } else {
      setRedirectMessage(t('welcome.excellentStartingSBA'));
      setTimeout(async () => {
        const redirectUrl = await buildRedirectUrl(
          import.meta.env.VITE_PFS_URL || 'https://pfs.corebusinesscapital.com',
          'pfs'
        );
        window.location.href = redirectUrl;
      }, 1500);
    }
  };

  // Redirect Loading State
  if (isGeneratingToken) {
    return (
      <Box 
        sx={{ 
          minHeight: '80vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'pulse 1.5s infinite',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.05)' },
              '100%': { transform: 'scale(1)' },
            },
          }}
        >
          <CircularProgress size={50} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {redirectMessage || t('welcome.preparingSecureConnection')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('welcome.redirectedInMoment')}
        </Typography>
      </Box>
    );
  }

  // Initial Step - Choose SBA or Faster Funding
  if (step === 'initial') {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
        {/* Welcome Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 5 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'text.primary', fontSize: { xs: '1.25rem', sm: '1.5rem' }, wordBreak: 'break-word' }}>
            {getGreeting()}, {user?.firstName || 'there'}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
            {t('welcome.letsFind')}
          </Typography>
        </Box>

        {/* Bank statements first: the easiest, most valuable thing a client can do */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <BankStatementUploader />
        </Box>

        {/* Journey Progress Indicator */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            border: '1px solid',
            borderColor: 'grey.200',
            overflowX: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              {t('welcome.yourFundingJourney')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.5, sm: 2 }, minWidth: 'fit-content' }}>
            {[t('welcome.start'), t('welcome.apply'), t('welcome.docs'), t('welcome.review'), t('welcome.funded')].map((stage, index) => (
              <React.Fragment key={stage}>
                <Box sx={{ textAlign: 'center', minWidth: { xs: 'auto', sm: 'auto' } }}>
                  <Box
                    sx={{
                      width: { xs: 28, sm: 40 },
                      height: { xs: 28, sm: 40 },
                      borderRadius: '50%',
                      bgcolor: index === 0 ? 'primary.main' : 'grey.300',
                      color: index === 0 ? 'white' : 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: { xs: '0.7rem', sm: '0.9rem' },
                      mx: 'auto',
                      mb: 0.5,
                    }}
                  >
                    {index === 0 ? <CheckCircle sx={{ fontSize: { xs: 16, sm: 20 } }} /> : index + 1}
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: index === 0 ? 600 : 400, fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                    {stage}
                  </Typography>
                </Box>
                {index < 4 && (
                  <Box
                    sx={{
                      width: { xs: 16, sm: 40 },
                      height: 3,
                      bgcolor: 'grey.300',
                      borderRadius: 2,
                      mt: -2,
                      flexShrink: 0,
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        </Paper>

        {/* Main Cards */}
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* SBA Funding Card */}
          <Card 
            sx={{ 
              flex: 1, 
              cursor: 'pointer',
              transition: 'all 0.3s',
              border: '2px solid transparent',
              borderRadius: 3,
              overflow: 'visible',
              position: 'relative',
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'translateY(-8px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
              }
            }}
            onClick={handleSBAClick}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -12,
                left: 20,
                px: 2,
                py: 0.5,
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 2,
                fontSize: '0.75rem',
                fontWeight: 700,
              }}
            >
              {t('welcome.lowestRates')}
            </Box>
            <CardContent sx={{ p: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
              <Box 
                sx={{ 
                  width: { xs: 70, sm: 100 }, 
                  height: { xs: 70, sm: 100 }, 
                  mx: 'auto', 
                  mb: { xs: 2, sm: 3 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #FBF3E6 0%, #F1DDBA 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AccountBalance sx={{ fontSize: { xs: 35, sm: 50 }, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                {t('welcome.usSbaLoan')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 }, minHeight: { xs: 'auto', sm: 48 } }}>
                {t('welcome.sbaDescription')}
              </Typography>
              
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                {[
                  { label: t('welcome.amounts'), value: '$250K - $5M+' },
                  { label: t('welcome.terms'), value: t('welcome.upTo25years') },
                  { label: t('welcome.rates'), value: t('welcome.primePlus2to3') },
                  { label: t('welcome.timeline'), value: t('welcome.45to90days') },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{item.value}</Typography>
                  </Box>
                ))}
              </Box>

              <Button 
                variant="contained" 
                size="large" 
                fullWidth
                endIcon={<ArrowForward />}
                sx={{ 
                  py: 1.5, 
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                {t('welcome.startSbaApplication')}
              </Button>
            </CardContent>
          </Card>

          {/* Faster Funding Card */}
          <Card 
            sx={{ 
              flex: 1, 
              cursor: 'pointer',
              transition: 'all 0.3s',
              border: '2px solid transparent',
              borderRadius: 3,
              overflow: 'visible',
              position: 'relative',
              '&:hover': {
                borderColor: 'success.main',
                transform: 'translateY(-8px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.success.main, 0.2)}`,
              }
            }}
            onClick={handleFasterFundingClick}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -12,
                left: 20,
                px: 2,
                py: 0.5,
                bgcolor: 'success.main',
                color: 'white',
                borderRadius: 2,
                fontSize: '0.75rem',
                fontWeight: 700,
              }}
            >
              {t('welcome.fastestApproval')}
            </Box>
            <CardContent sx={{ p: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
              <Box 
                sx={{ 
                  width: { xs: 70, sm: 100 }, 
                  height: { xs: 70, sm: 100 }, 
                  mx: 'auto', 
                  mb: { xs: 2, sm: 3 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Speed sx={{ fontSize: { xs: 35, sm: 50 }, color: 'success.main' }} />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                {t('welcome.fastFunding')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 }, minHeight: { xs: 'auto', sm: 48 } }}>
                {t('welcome.fastFundingDescription')}
              </Typography>
              
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                {[
                  { label: t('welcome.amounts'), value: '$15K - $2M' },
                  { label: t('welcome.terms'), value: '6 - 60 months' },
                  { label: t('welcome.rates'), value: t('welcome.competitive') },
                  { label: t('welcome.timeline'), value: t('welcome.24to72hours') },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{item.value}</Typography>
                  </Box>
                ))}
              </Box>

              <Button 
                variant="contained" 
                color="success"
                size="large" 
                fullWidth
                endIcon={<ArrowForward />}
                sx={{ 
                  py: 1.5, 
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                {t('welcome.getFastFunding')}
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Help Section */}
        <Box 
          sx={{ 
            mt: { xs: 3, sm: 4 }, 
            p: { xs: 2, sm: 3 }, 
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            border: '1px dashed',
            borderColor: 'info.main',
          }}
        >
          <HelpOutline sx={{ color: 'info.main', mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('welcome.notSureWhich')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('welcome.answer3Questions')}
          </Typography>
          <Button 
            variant="outlined" 
            color="info"
            onClick={() => setStep('sba-q1')}
            sx={{ borderRadius: 2 }}
          >
            {t('welcome.takeTheQuiz')}
          </Button>
        </Box>
      </Box>
    );
  }

  // SBA Question Flow - Q1: Tax Returns
  if (step === 'sba-q1') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 3, sm: 4 } }}>
            <IconButton onClick={() => setStep('initial')} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('welcome.sbaQualification')}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={33} 
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {t('welcome.questionOf', { current: 1, total: 3 })}
            </Typography>
          </Box>

          {/* Question */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '2.5rem' }}>📋</Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              {t('welcome.taxReturnsQuestion')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('welcome.taxReturnsInfo')}
            </Typography>
          </Box>

          {/* Answer Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={hasTaxReturns === 'yes' ? 'contained' : 'outlined'}
              size="large"
              fullWidth
              onClick={() => handleTaxReturnsAnswer('yes')}
              sx={{ 
                py: 2, 
                borderRadius: 2,
                borderWidth: 2,
                '&:hover': { borderWidth: 2 },
              }}
            >
              {t('welcome.yesIHaveThem')}
            </Button>
            <Button
              variant={hasTaxReturns === 'no' ? 'contained' : 'outlined'}
              color="inherit"
              size="large"
              fullWidth
              onClick={() => handleTaxReturnsAnswer('no')}
              sx={{ 
                py: 2, 
                borderRadius: 2,
                borderWidth: 2,
                borderColor: 'grey.300',
                '&:hover': { borderWidth: 2, borderColor: 'grey.400' },
              }}
            >
              {t('welcome.notYet')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // SBA Question Flow - Q2: Credit Score
  if (step === 'sba-q2') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 3, sm: 4 } }}>
            <IconButton onClick={() => setStep('sba-q1')} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('welcome.sbaQualification')}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={66} 
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {t('welcome.questionOf', { current: 2, total: 3 })}
            </Typography>
          </Box>

          {/* Question */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TrendingUp sx={{ fontSize: '2rem', color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              {t('welcome.creditScoreQuestion')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('welcome.creditScoreInfo')}
            </Typography>
          </Box>

          {/* Answer Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={hasCreditScore === 'yes' ? 'contained' : 'outlined'}
              size="large"
              fullWidth
              onClick={() => handleCreditScoreAnswer('yes')}
              sx={{ 
                py: 2, 
                borderRadius: 2,
                borderWidth: 2,
                '&:hover': { borderWidth: 2 },
              }}
            >
              {t('welcome.yes700Plus')}
            </Button>
            <Button
              variant={hasCreditScore === 'no' ? 'contained' : 'outlined'}
              color="inherit"
              size="large"
              fullWidth
              onClick={() => handleCreditScoreAnswer('no')}
              sx={{ 
                py: 2, 
                borderRadius: 2,
                borderWidth: 2,
                borderColor: 'grey.300',
                '&:hover': { borderWidth: 2, borderColor: 'grey.400' },
              }}
            >
              {t('welcome.below700')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // SBA Question Flow - Q3: Outstanding Debt
  if (step === 'sba-q3') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 3, sm: 4 } }}>
            <IconButton onClick={() => setStep('sba-q2')} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('welcome.sbaQualification')}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={100} 
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {t('welcome.questionOf', { current: 3, total: 3 })}
            </Typography>
          </Box>

          {/* Question */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '2.5rem' }}>💳</Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              {t('welcome.debtQuestion')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('welcome.debtInfo')}
            </Typography>
          </Box>

          {/* Answer Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={hasOutstandingDebt === 'no' ? 'contained' : 'outlined'}
              size="large"
              fullWidth
              onClick={() => handleOutstandingDebtAnswer('no')}
              sx={{ 
                py: 2, 
                borderRadius: 2,
                borderWidth: 2,
                '&:hover': { borderWidth: 2 },
              }}
            >
              {t('welcome.noDebtFree')}
            </Button>
            <Button
              variant={hasOutstandingDebt === 'yes' ? 'contained' : 'outlined'}
              color="inherit"
              size="large"
              fullWidth
              onClick={() => handleOutstandingDebtAnswer('yes')}
              sx={{ 
                py: 2, 
                borderRadius: 2,
                borderWidth: 2,
                borderColor: 'grey.300',
                '&:hover': { borderWidth: 2, borderColor: 'grey.400' },
              }}
            >
              {t('welcome.yesIHaveSome')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return null;
};

export default WelcomeFlow;
